import { Stack, Typography } from '@mui/material';

import Dialog from '../../../components/Dialog';
import Button from '../../../components/styledComponents/Button';
import Link from '../../../components/styledComponents/Link';
import colors from '../../../constants/colors';
import useAnalytics from '../../../hooks/useAnalytics';

interface WithOutAutoPayDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: any) => void;
  isLoading: { autoPay: boolean; payment: boolean };
  submitAndSetupAutoPay: (values: any) => void;
  buttonValue: string;
}

const WithOutAutoPayDialog = ({
  open,
  onClose,
  onSubmit,
  isLoading,
  submitAndSetupAutoPay,
  buttonValue,
}: WithOutAutoPayDialogProps) => {
  const { track } = useAnalytics();

  return (
    <Dialog
      closeDialog={() => {
        track('Link Accessed', {
          event_type: 'Link Accessed',
          link: 'X',
          location: 'Feature Card',
        });
        onClose();
      }}
      open={open}
      title="You’re submitting a payment without setting up AutoPay"
      sx={{
        '& .MuiDialogContent-root': {
          mt: { mobile: 2, tablet: 3 },
        },
      }}
    >
      <Typography
        sx={{
          fontFamily: 'Cabin',
          fontSize: { mobile: 14, tablet: 18 },
          lineHeight: { mobile: '16px', tablet: '18px' },
          letterSpacing: { mobile: 0.2, tablet: 0.4 },
          color: colors.csaaDark,
          mx: { tablet: 1 },
        }}
      >
        Set up AutoPay and never worry about missing a payment. It’s a
        convenient and secure way to pay. <br />
        <br /> With AutoPay, future payments for this policy will be
        automatically withdrawn on the due date with the selected payment
        method.
      </Typography>
      <Stack sx={{ mt: { mobile: 3, tablet: 5 } }} spacing={1}>
        <Button
          fullWidth
          variant="outlined"
          color="inherit"
          data-testid="withOutAutoPayDialogSubmitAndSetUpAutoPayButton"
          sx={{
            borderWidth: 2,
            fontWeight: 700,
            textTransform: 'none',
            color: 'text.secondary',
            px: 4,
            py: 0.5,
          }}
          onClick={submitAndSetupAutoPay}
          loading={isLoading.autoPay}
        >
          Submit payment and set up AutoPay
        </Button>
        <Button fullWidth onClick={onSubmit} loading={isLoading.payment}>
          {buttonValue}
        </Button>
      </Stack>
      <Typography
        sx={{
          fontSize: { mobile: 16, tablet: 18 },
          fontWeight: 400,
          fontFamily: 'Cabin',
          mb: 3,
          color: colors.csaaDark,
          mt: { mobile: 1, tablet: 2 },
        }}
      >
        By setting up AutoPay, you agree to the{' '}
        <Link
          to="https://csaa-insurance.aaa.com/content/aaa-ie/b2c/en/misc/auth-terms.html"
          target="_blank"
          onClick={() =>
            track('Terms and Conditions Accessed', {
              event_type: 'Link Accessed',
              link: 'paperless preferences terms and conditions',
              location: 'Modal',
            })
          }
          underline="none"
          sx={{
            color: 'primary',
            fontWeight: { mobile: 400, tablet: 700 },
            fontSize: { mobile: 16, tablet: 18 },
          }}
        >
          AutoPay Terms and Conditions.
        </Link>{' '}
      </Typography>
    </Dialog>
  );
};

export default WithOutAutoPayDialog;
